/**
 * remplacementPartout — ce que le mode partout de `replace_text`
 * (`everywhere:true`) trouve dans l'acte, ce qu'il en fait un plan, et ce qu'il
 * refuse. Pur : il ne connaît ni l'éditeur ni l'export, seulement l'acte lu au
 * fichier et les couples de l'appel ; ses décisions se testent sans navigateur.
 * Ce qui parle à l'éditeur est dans `replaceEverywhereExecutor.ts`, dont
 * l'EN-TÊTE dit les règles du mode et ce qui est mesuré ou non.
 *
 * ## Les deux garde-fous
 *
 * 1. **Le mot entier** ({@link estUneOccurrenceEntiere}) : « 1 500 » n'est pas
 *    trouvé dans « 21 500 », « 11 500 », « 1 500,50 » ni « 1 500 000 », ni
 *    « 50 € » dans « 1 500,50 € ». C'est la règle de `format_text` sur tout
 *    l'acte (`estUnMotEntier`, `planDeFormatText.ts`), DURCIE ici de ce qu'un
 *    remplacement exige en plus : la décimale se garde des deux côtés, et le
 *    texte est jugé sans ses espaces de bord.
 * 2. **Le compte attendu** ({@link verdictDesComptes}) : chaque couple dit
 *    combien de fois l'agent a lu son texte. Un seul compte différent, et RIEN
 *    n'est écrit pour tout l'appel ; le refus rend les emplacements.
 *
 * ## Le texte de l'agent n'est pas celui du document
 *
 * Le modèle recopie « 1 500 € » avec des espaces simples quand l'acte porte une
 * espace insécable ou fine, et une apostrophe droite pour une courbe. La
 * recherche se fait donc sur des textes PLIÉS ({@link plierLaTypographie}),
 * caractère pour caractère : les positions trouvées valent sur le texte
 * d'origine. Deux conséquences :
 *
 * - l'adresse d'une étape porte le texte DU DOCUMENT (la tranche d'origine),
 *   jamais celui de l'agent : l'éditeur cherche ce que le fichier écrit ;
 * - le remplacement reprend la nature des espaces et des apostrophes du
 *   document quand l'agent a écrit les siennes simples et en même nombre
 *   ({@link remplacementPour}) : un montant garde ses insécables (décision du
 *   porteur, 2026-10-08). Une espace insécable ou une apostrophe courbe écrite
 *   par l'agent dans `replace` est une demande : elle part telle quelle ;
 * - une occurrence qui se lit déjà comme son remplacement fait refuser tout
 *   l'appel (`REPLACE_EVERYWHERE_NOTHING_TO_CHANGE`) : l'éditeur refuserait une
 *   étape sans effet, et le compte attendu la contenait.
 *
 * ## Ce que ce mode n'écrit pas encore
 *
 * {@link raisonHorsDePortee} est le SEUL endroit qui décide qu'une occurrence
 * trouvée ne s'écrit pas. Une seule occurrence hors de portée fait refuser tout
 * l'appel (`REPLACE_EVERYWHERE_OUT_OF_REACH`), bloc nommé. Deux de ses raisons
 * sont des limites de MESURE, pas de conception — la sonde
 * `tests/ws-mocked/mesure-replace-text-partout.spec.ts` dira s'il faut les lever :
 *
 * - `not_unique_in_block` : le texte du document n'est pas unique dans son
 *   paragraphe, à la typographie près (même montant deux fois, ou une fois
 *   entier et une fois dans un nombre plus long). L'adresser demanderait une étape par POSITION, que
 *   l'éditeur n'a reçue que pour une mise en forme (`motifDePosition`,
 *   `planDeFormatText.ts`) — jamais pour un `text.rewrite` (R2, R3 de la sonde).
 * - `table_cell` : un `text.rewrite` dans un paragraphe de cellule visé par son
 *   identifiant n'est pas mesuré (R5) ; seule la mise en forme l'est (§16 de
 *   `superdoc-document-api.md`).
 */

import {
  CANVAS_REPLACE_EXCERPT_MAX,
  CANVAS_REPLACE_LOCATIONS_MAX,
  estUneApostropheDeTexte,
  estUneEspaceDeTexte,
  plierLaTypographie,
  type CanvasReplaceEverywhereReceipt,
  type CanvasReplaceOutOfReachReason,
} from '@hector/shared-types';
import { estUnMotEntier, texteDuParagraphe } from './planDeFormatText';
import { occurrencesDuPassage } from './verificationDeForme';

/** Un paragraphe où chercher : ce que le lecteur de forme en rend, réduit à ce que ce mode lit. Un `ParagrapheVise` le satisfait. */
export interface ParagrapheARemplacer {
  /** L'identifiant du fichier (`w14:paraId`) ; `null` : aucun, ou partagé avec un autre paragraphe. */
  nodeId: string | null;
  /** L'identifiant que le fichier lui donne, quand `nodeId` est nul parce qu'un autre paragraphe le porte aussi. */
  identifiantEnDouble?: string;
  enCellule: boolean;
  passages: ReadonlyArray<{ texte: string }>;
}

/** L'acte où chercher : le corps avec ses cellules de tableau, dans l'ordre du document. Un `ActeVise` le satisfait. */
export interface ActeARemplacer {
  paragraphes: ReadonlyArray<ParagrapheARemplacer>;
}

/** Un couple de l'appel : le texte cherché, son remplacement, et le nombre de fois où l'agent l'a lu. */
export interface CoupleARemplacer {
  find: string;
  replace: string;
  expectedCount: number;
}

/** Une occurrence en mot entier d'un couple, là où le fichier la porte. */
export interface OccurrenceARemplacer {
  /** Rang du couple dans l'appel. */
  couple: number;
  /** Rang du paragraphe dans l'acte. */
  paragraphe: number;
  nodeId: string | null;
  enCellule: boolean;
  debut: number;
  /** Exclue. */
  fin: number;
  /** Le texte DU DOCUMENT à cet endroit — ses espaces insécables et ses apostrophes, pas ceux de l'agent. */
  texte: string;
  /** Ce qui l'entoure, pour qu'un refus la situe. */
  extrait: string;
  /** Le texte à écrire à sa place. */
  remplacement: string;
  /** Présente : ce mode ne peut pas l'écrire, et pourquoi. */
  horsDePortee?: CanvasReplaceOutOfReachReason;
}

/** Ce que l'acte porte d'un couple. */
export interface ReleveDUnCouple {
  /** Occurrences en mot entier, hors de portée comprises. */
  trouvees: number;
  /** Fois où le texte n'apparaît que dans un mot ou un nombre plus long. */
  accolees: number;
  /** Occurrences laissées au texte plus long d'un autre couple, qui les couvre ou les chevauche. */
  couvertes: number;
}

/** Tout ce que l'acte porte des couples d'un appel. */
export interface ReleveDuRemplacement {
  /** Les occurrences gardées, dans l'ordre du document, sans chevauchement. */
  occurrences: OccurrenceARemplacer[];
  /** Un relevé par couple, au rang du couple. */
  parCouple: ReleveDUnCouple[];
}

/** Une étape `text.rewrite` telle que l'éditeur la reçoit. */
export interface EtapeDeRemplacement {
  id: string;
  op: 'text.rewrite';
  where: {
    by: 'select';
    select: { type: 'text'; pattern: string; caseSensitive: true };
    within: { kind: 'block'; nodeType: 'paragraph'; nodeId: string };
    require: 'exactlyOne';
  };
  args: { replacement: { text: string } };
}

/** Une étape du plan, avec l'occurrence qu'elle écrit : c'est par elle qu'un refus de l'éditeur retrouve son bloc. */
export interface EtapeEtSonOccurrence {
  etape: EtapeDeRemplacement;
  occurrence: OccurrenceARemplacer & { nodeId: string };
}

/** Le refus d'un appel entier, avant toute écriture. */
export interface RefusDuRemplacement {
  code: 'REPLACE_EVERYWHERE_COUNT_MISMATCH' | 'REPLACE_EVERYWHERE_NOTHING_TO_CHANGE' | 'REPLACE_EVERYWHERE_OUT_OF_REACH';
  message: string;
  /** Les couples à trancher, tels que le reçu les rend. */
  pairs: CanvasReplaceEverywhereReceipt['pairs'];
}

/** Nombre maximal de couples décrits dans le MESSAGE d'un refus ; le reçu (`replaceEverywhere.pairs`) les porte tous. Valeur de jugement. */
const COUPLES_DECRITS_MAX = 10;

/** Caractères de contexte gardés de chaque côté d'une occurrence dans son extrait. Valeur de jugement. */
const CONTEXTE_DE_L_EXTRAIT = 30;

/** Coupe un texte à la longueur que le reçu admet, points de suspension compris. */
const borner = (texte: string): string => (texte.length <= CANVAS_REPLACE_EXCERPT_MAX ? texte : `${texte.slice(0, CANVAS_REPLACE_EXCERPT_MAX - 1)}…`);

/** Un texte tel qu'un message de refus le recopie : entre guillemets, borné. */
const citer = (texte: string): string => JSON.stringify(borner(texte));

/**
 * L'extrait qui situe `[debut, fin[` dans le texte de son paragraphe : le
 * passage et {@link CONTEXTE_DE_L_EXTRAIT} caractères de chaque côté, borné à
 * la longueur que le reçu admet.
 */
function extrait(texte: string, debut: number, fin: number): string {
  const depuis = Math.max(0, debut - CONTEXTE_DE_L_EXTRAIT);
  const jusqua = Math.min(texte.length, fin + CONTEXTE_DE_L_EXTRAIT);
  return borner(`${depuis > 0 ? '…' : ''}${texte.slice(depuis, jusqua)}${jusqua < texte.length ? '…' : ''}`);
}

/**
 * Donne aux caractères d'une famille (espaces, ou apostrophes) de `replace` la
 * nature de ceux du document, rang pour rang — seulement si l'agent les a tous
 * écrits simples (`simple`) et en même nombre que le document. Sinon `replace`
 * est rendu tel quel : une forme typographique écrite par l'agent est une
 * demande, et un nombre différent ne dit pas quel caractère irait où.
 */
function transposer(texteDuDocument: string, replace: string, estDeLaFamille: (caractere: string) => boolean, simple: string): string {
  const duDocument = [...texteDuDocument].filter(estDeLaFamille);
  const duRemplacement = [...replace].filter(estDeLaFamille);
  if (duDocument.length === 0 || duRemplacement.length !== duDocument.length || duRemplacement.some((caractere) => caractere !== simple)) return replace;
  let rang = 0;
  return [...replace].map((caractere) => (estDeLaFamille(caractere) ? duDocument[rang++]! : caractere)).join('');
}

/**
 * Le texte à écrire à la place d'une occurrence : `replace`, avec les espaces
 * et les apostrophes du document quand l'agent a recopié les siennes simples
 * ({@link transposer}). « 1 500 € » écrit au fichier avec une fine insécable et
 * une insécable devient « 2 300 € » avec les mêmes ; « l’article 700 » devient
 * « l’article 699 ».
 *
 * WHY seulement des caractères simples : sans cette condition, un agent qui
 * demande d'écrire une insécable là où le document porte une espace simple
 * recevait un reçu vert sur un texte inchangé — l'espace du document remplaçait
 * celle qu'il demandait.
 *
 * @param texteDuDocument La tranche du document que l'occurrence couvre.
 */
export function remplacementPour(texteDuDocument: string, replace: string): string {
  return transposer(texteDuDocument, transposer(texteDuDocument, replace, estUneEspaceDeTexte, ' '), estUneApostropheDeTexte, "'");
}

/** Une plage candidate d'un couple dans un texte plié. */
interface PlageDUnCouple {
  couple: number;
  debut: number;
  fin: number;
}

const COMMENCE_PAR_UN_CHIFFRE = /^\p{N}/u;
const FINIT_PAR_UN_CHIFFRE = /\p{N}$/u;
const COMMENCE_PAR_UNE_DECIMALE = /^[,.]\p{N}/u;
const FINIT_PAR_UN_CHIFFRE_ET_SA_MARQUE_DECIMALE = /\p{N}[,.]$/u;

/**
 * Vrai si `[debut, fin[` d'un texte PLIÉ est une occurrence qu'un remplacement
 * peut écrire : un mot entier au sens de `format_text` (`estUnMotEntier`), et
 * deux exigences de plus, propres à ce mode.
 *
 * 1. **Le texte est jugé sans ses espaces de bord.** `estUnMotEntier` n'exige
 *    une frontière que du côté où le texte finit par une lettre ou un chiffre :
 *    « 1 500 » suivi d'une espace n'en demandait donc aucune à droite et se
 *    trouvait dans « 1 500 000 € ». Le contrat refuse déjà ces textes
 *    (`verifierLeRemplacementDeTexte`, `actionCatalog.ts`) ; la recherche ne
 *    s'en remet pas à lui.
 * 2. **Une décimale ne se coupe d'aucun côté.** `estUnMotEntier` protège la
 *    droite d'un nombre (« 1 500 » dans « 1 500,50 ») mais, à gauche, seulement
 *    un groupe de milliers : « 50 € » se trouvait dans « 1 500,50 € », « 20 »
 *    dans « 1,20 ». Ici, un texte qui commence par un chiffre n'est pas une
 *    occurrence après un chiffre et sa virgule ou son point ; ni un texte qui
 *    commence par la marque décimale (« ,50 € ») après un chiffre ; ni un texte
 *    qui finit par elle (« 1 500, ») devant un chiffre.
 *
 * WHY ici et pas dans `estUnMotEntier` : une forme posée sur la décimale d'un
 * montant se voit et s'annule ; un montant FAUX écrit sous un reçu vert est le
 * pire résultat de ce mode. Durcir la règle commune changerait `format_text`,
 * hors du périmètre de ce changement. Le prix d'une exigence de trop est un
 * refus de compte, qui nomme les blocs : « 2024 » dans « 01.02.2024 » n'est pas
 * une occurrence.
 */
function estUneOccurrenceEntiere(plie: string, debut: number, fin: number): boolean {
  let [de, a] = [debut, fin];
  while (de < a && plie[de] === ' ') de += 1;
  while (a > de && plie[a - 1] === ' ') a -= 1;
  if (de === a || !estUnMotEntier(plie, de, a)) return false;
  const [texte, avant, apres] = [plie.slice(de, a), plie.slice(0, de), plie.slice(a)];
  if (COMMENCE_PAR_UN_CHIFFRE.test(texte) && FINIT_PAR_UN_CHIFFRE_ET_SA_MARQUE_DECIMALE.test(avant)) return false;
  if (COMMENCE_PAR_UNE_DECIMALE.test(texte) && FINIT_PAR_UN_CHIFFRE.test(avant)) return false;
  if (FINIT_PAR_UN_CHIFFRE_ET_SA_MARQUE_DECIMALE.test(texte) && COMMENCE_PAR_UN_CHIFFRE.test(apres)) return false;
  return true;
}

/** Les occurrences entières ({@link estUneOccurrenceEntiere}) d'un texte plié dans un texte plié, de gauche à droite, sans chevauchement entre elles ; une occurrence accolée n'empêche pas d'en trouver une qui la chevauche. */
function plagesDuCouple(plie: string, cherche: string, couple: number): { plages: PlageDUnCouple[]; accolees: number } {
  const plages: PlageDUnCouple[] = [];
  let accolees = 0;
  for (let debut = plie.indexOf(cherche); debut >= 0;) {
    const fin = debut + cherche.length;
    const entier = estUneOccurrenceEntiere(plie, debut, fin);
    if (entier) plages.push({ couple, debut, fin });
    else accolees += 1;
    debut = plie.indexOf(cherche, entier ? fin : debut + 1);
  }
  return { plages, accolees };
}

/**
 * Pourquoi une occurrence trouvée ne s'écrit pas — LE point d'extension du
 * mode (voir l'en-tête, « Ce que ce mode n'écrit pas encore »).
 *
 * - Sans identifiant, ou à l'identifiant partagé : aucune étape ne l'adresse
 *   (même règle que les verbes de forme, `corpsAvecSesCellules`).
 * - En cellule de tableau : non mesuré (R5). L'ouvrir, c'est retirer cette
 *   ligne après la sonde — l'étape est la même que dans le corps.
 * - Texte non unique dans son paragraphe (`unique` faux) : il faudrait une
 *   adresse par position, non mesurée pour un `text.rewrite` (R2, R3).
 *   L'ouvrir, c'est rendre ici `undefined` et donner à
 *   {@link etapesDeRemplacement} l'adresse par position de `planDeFormatText.ts`.
 */
function raisonHorsDePortee(paragraphe: ParagrapheARemplacer, unique: boolean): CanvasReplaceOutOfReachReason | undefined {
  if (paragraphe.nodeId === null) return paragraphe.identifiantEnDouble === undefined ? 'no_node_id' : 'duplicate_node_id';
  if (paragraphe.enCellule) return 'table_cell';
  return unique ? undefined : 'not_unique_in_block';
}

/**
 * Pour chaque occurrence d'un paragraphe, de la dernière à la première : vrai
 * si son texte désigne UN seul endroit du paragraphe, que l'éditeur résolve
 * toutes les étapes sur le texte d'origine ou l'une après l'autre dans l'ordre
 * émis ({@link etapesDeRemplacement} : de droite à gauche).
 *
 * WHY les deux lectures : l'éditeur ne dit pas laquelle il fait (R3 de la
 * sonde). Sur le texte d'origine, un montant présent deux fois — ou une fois
 * entier et une fois dans un nombre plus long — est ambigu. En séquence, le
 * remplacement d'un couple peut écrire le texte que cherche un autre (échange
 * A → B et B → A dans le même paragraphe). Les chevauchements comptent : deux
 * occurrences qui se chevauchent ne désignent toujours pas un seul endroit.
 *
 * WHY sur des textes PLIÉS, motif compris : rien ne dit que l'éditeur distingue
 * une espace insécable d'une espace simple quand il cherche (R1b de la sonde).
 * « 1 500 € » écrit une fois avec une insécable et une fois sans, dans le même
 * paragraphe, serait unique à la lettre et double pour lui.
 *
 * @param plie Le texte du paragraphe, plié ({@link plierLaTypographie}).
 */
function unicitesDansLeParagraphe(plie: string, duParagraphe: ReadonlyArray<Pick<OccurrenceARemplacer, 'debut' | 'fin' | 'texte' | 'remplacement'>>): Map<number, boolean> {
  const unicites = new Map<number, boolean>();
  let courant = plie;
  for (const occurrence of [...duParagraphe].sort((a, b) => b.debut - a.debut)) {
    const motif = plierLaTypographie(occurrence.texte);
    // À gauche de l'occurrence, rien n'a encore été réécrit : sa position d'origine vaut sur le texte courant.
    unicites.set(occurrence.debut, occurrencesDuPassage(plie, motif) === 1
      && occurrencesDuPassage(courant, motif) === 1
      && courant.indexOf(motif) === occurrence.debut);
    courant = `${courant.slice(0, occurrence.debut)}${plierLaTypographie(occurrence.remplacement)}${courant.slice(occurrence.fin)}`;
  }
  return unicites;
}

/**
 * Toutes les occurrences des couples dans l'acte : sensible à la casse, en MOT
 * ENTIER ({@link estUneOccurrenceEntiere}), dans le texte que l'avocat voit, à
 * l'intérieur d'UN paragraphe, à la typographie près ({@link plierLaTypographie}).
 *
 * Là où les textes de deux couples se chevauchent, le plus long garde
 * l'occurrence — à longueur égale, la plus à gauche, puis le premier couple —
 * et l'autre la compte `couvertes` : elle ne sera pas écrite pour lui, et son
 * compte attendu ne la contient donc pas (même règle que les listes de textes
 * de `format_text`, `planDeFormatText.ts`). Les couples ne s'enchaînent pas :
 * tout est cherché sur le texte d'avant, « A → B » et « B → A » s'échangent.
 *
 * Portée : les paragraphes de l'acte passé — le corps et ses cellules de
 * tableau (`corpsAvecSesCellules`). Le texte d'un en-tête, d'un pied ou d'une
 * note n'y est ni cherché ni compté.
 *
 * @param couples Les couples de l'appel, aux `find` distincts une fois pliés.
 */
export function occurrencesARemplacer(acte: ActeARemplacer, couples: readonly CoupleARemplacer[]): ReleveDuRemplacement {
  const cherches = couples.map((couple) => plierLaTypographie(couple.find));
  const parCouple = couples.map((): ReleveDUnCouple => ({ trouvees: 0, accolees: 0, couvertes: 0 }));
  const occurrences: OccurrenceARemplacer[] = [];
  acte.paragraphes.forEach((paragraphe, rang) => {
    const texte = texteDuParagraphe(paragraphe);
    const plie = plierLaTypographie(texte);
    const candidates = cherches.flatMap((cherche, couple) => {
      const { plages, accolees } = plagesDuCouple(plie, cherche, couple);
      parCouple[couple]!.accolees += accolees;
      return plages;
    }).sort((a, b) => (b.fin - b.debut) - (a.fin - a.debut) || a.debut - b.debut || a.couple - b.couple);
    const gardees: PlageDUnCouple[] = [];
    for (const candidate of candidates) {
      if (gardees.some((gardee) => gardee.debut < candidate.fin && candidate.debut < gardee.fin)) parCouple[candidate.couple]!.couvertes += 1;
      else gardees.push(candidate);
    }
    const duParagraphe = gardees.sort((a, b) => a.debut - b.debut).map(({ couple, debut, fin }) => {
      const duDocument = texte.slice(debut, fin);
      return { couple, debut, fin, texte: duDocument, remplacement: remplacementPour(duDocument, couples[couple]!.replace) };
    });
    const unicites = unicitesDansLeParagraphe(plie, duParagraphe);
    for (const occurrence of duParagraphe) {
      parCouple[occurrence.couple]!.trouvees += 1;
      const horsDePortee = raisonHorsDePortee(paragraphe, unicites.get(occurrence.debut) === true);
      occurrences.push({
        ...occurrence,
        paragraphe: rang,
        nodeId: paragraphe.nodeId ?? paragraphe.identifiantEnDouble ?? null,
        enCellule: paragraphe.enCellule,
        extrait: extrait(texte, occurrence.debut, occurrence.fin),
        ...(horsDePortee === undefined ? {} : { horsDePortee }),
      });
    }
  });
  return { occurrences, parCouple };
}

/** Vrai pour une occurrence qu'une étape peut adresser : dans la portée, donc avec un identifiant. */
const estAEcrire = (occurrence: OccurrenceARemplacer): occurrence is OccurrenceARemplacer & { nodeId: string } => occurrence.horsDePortee === undefined && occurrence.nodeId !== null;

/**
 * Ce que le reçu rend d'un couple à trancher : ses comptes, et les emplacements donnés.
 *
 * @param horsDePortee Combien de ses occurrences trouvées ce mode ne peut pas écrire.
 */
function coupleAuRecu(
  couple: CoupleARemplacer,
  releve: ReleveDUnCouple,
  emplacements: readonly OccurrenceARemplacer[],
  horsDePortee: number,
): CanvasReplaceEverywhereReceipt['pairs'][number] {
  const rendus = emplacements.slice(0, CANVAS_REPLACE_LOCATIONS_MAX);
  return {
    find: borner(couple.find),
    replace: borner(couple.replace),
    expectedCount: couple.expectedCount,
    found: releve.trouvees,
    ...(releve.accolees > 0 ? { insideLongerWord: releve.accolees } : {}),
    ...(horsDePortee > 0 ? { outOfReach: horsDePortee } : {}),
    ...(releve.couvertes > 0 ? { coveredByLongerPair: releve.couvertes } : {}),
    locations: rendus.map((occurrence) => ({
      nodeId: occurrence.nodeId,
      excerpt: occurrence.extrait,
      ...(occurrence.enCellule ? { inTable: true as const } : {}),
      ...(occurrence.horsDePortee === undefined ? {} : { outOfReach: occurrence.horsDePortee }),
    })),
    ...(emplacements.length > rendus.length ? { locationsNotListed: emplacements.length - rendus.length } : {}),
  };
}

/** Les blocs d'un couple, nommés pour un message : identifiants distincts, bornés. */
function direLesBlocs(emplacements: readonly OccurrenceARemplacer[]): string {
  const noms = [...new Set(emplacements.map((occurrence) => (
    `${occurrence.nodeId ?? 'a paragraph without identifier'}${occurrence.enCellule ? ' (table cell)' : ''}`
  )))];
  const rendus = noms.slice(0, CANVAS_REPLACE_LOCATIONS_MAX);
  return `${rendus.join(', ')}${noms.length > rendus.length ? ` and ${noms.length - rendus.length} more` : ''}`;
}

/** Les premières descriptions d'une liste, et le compte de celles qui ne sont dites que par le reçu. */
function direLesCouples(descriptions: readonly string[]): string {
  const dites = descriptions.slice(0, COUPLES_DECRITS_MAX);
  return `${dites.join(' ')}${descriptions.length > dites.length ? ` ${descriptions.length - dites.length} more text(s) are listed in replaceEverywhere.pairs.` : ''}`;
}

/** Ce que tout reçu du mode dit de sa portée : la phrase est la même sur un refus et sur une écriture. */
export const PORTEE_DITE = 'Headers, footers and footnotes were not searched.';

/** Ce qu'il faut faire d'une occurrence hors de portée, raison par raison. */
const SUITE_PAR_RAISON: Readonly<Record<CanvasReplaceOutOfReachReason, string>> = {
  not_unique_in_block: 'its text is not unique in that block (it occurs several times there — counting inside a longer word or number, and whatever the kind of its spaces — or another edit of this call writes it there)',
  table_cell: 'it sits in a table cell, which this mode does not write yet',
  no_node_id: 'the document file gives its paragraph no identifier',
  duplicate_node_id: 'another paragraph of the document carries the same identifier',
};

/**
 * Décide si l'appel s'écrit : tous les comptes sont ceux attendus, chaque
 * occurrence change quelque chose ET chaque occurrence est à portée. Sinon le
 * refus, pour TOUT l'appel — un compte faux d'abord (l'agent doit trancher ce
 * qu'il a lu), puis ce qui se lit déjà comme son remplacement, puis ce qui est
 * hors de portée.
 *
 * @returns `ecrire` : toutes les occurrences, dans l'ordre du document ; ou `refus`.
 */
export function verdictDesComptes(
  couples: readonly CoupleARemplacer[],
  releve: ReleveDuRemplacement,
): { ecrire: Array<OccurrenceARemplacer & { nodeId: string }> } | { refus: RefusDuRemplacement } {
  const duCouple = (rang: number): OccurrenceARemplacer[] => releve.occurrences.filter((occurrence) => occurrence.couple === rang);
  const fautifs = couples.flatMap((couple, rang) => (releve.parCouple[rang]!.trouvees === couple.expectedCount ? [] : [rang]));
  if (fautifs.length > 0) {
    const descriptions = fautifs.map((rang) => {
      const couple = couples[rang]!;
      const { trouvees, accolees, couvertes } = releve.parCouple[rang]!;
      const emplacements = duCouple(rang);
      return `${citer(couple.find)}: expected ${couple.expectedCount}, found ${trouvees}`
        + (trouvees > 0 ? ` (blocks ${direLesBlocs(emplacements)})` : '')
        + (accolees > 0 ? `; it appears ${accolees} time(s) only inside a longer word or number (e.g. "1 500" inside "21 500"), which is not an occurrence` : '')
        + (couvertes > 0 ? `; ${couvertes} more occurrence(s) are covered by the longer find of another edit of this call and are replaced by that edit` : '')
        + (trouvees === 0 && accolees === 0 && couvertes === 0 ? ' — copy the text exactly as the document reads (case-sensitive)' : '')
        + '.';
    });
    return {
      refus: {
        code: 'REPLACE_EVERYWHERE_COUNT_MISMATCH',
        message: `replace_text everywhere wrote nothing: ${fautifs.length} of ${couples.length} text(s) do not occur the expected number of times as a whole word in the body and its tables. `
          + `${direLesCouples(descriptions)} `
          + 'Nothing was written and your scope is intact. For each text found at least once: if EVERY location found must change, resend the whole call with its expectedCount set to the count found; '
          + 'otherwise remove that edit from the call and change only the blocks you want with apply_edit_plan (one text_rewrite step per block). '
          + `The locations found are in replaceEverywhere.pairs[].locations. ${PORTEE_DITE}`,
        pairs: fautifs.map((rang) => {
          const emplacements = duCouple(rang);
          return coupleAuRecu(couples[rang]!, releve.parCouple[rang]!, emplacements, emplacements.filter((occurrence) => !estAEcrire(occurrence)).length);
        }),
      },
    };
  }
  const sansEffet = releve.occurrences.filter((occurrence) => occurrence.remplacement === occurrence.texte);
  if (sansEffet.length > 0) return { refus: refusSansEffet(couples, releve, sansEffet) };
  const horsDePortee = releve.occurrences.filter((occurrence) => !estAEcrire(occurrence));
  if (horsDePortee.length > 0) return { refus: refusHorsDePortee(couples, releve, horsDePortee.map((occurrence) => ({ occurrence }))) };
  return { ecrire: releve.occurrences.filter(estAEcrire) };
}

/**
 * Le refus d'un appel dont des occurrences se lisent DÉJÀ comme leur
 * remplacement : `find` et `replace` ne diffèrent alors que par la nature d'une
 * espace ou d'une apostrophe, et le document porte déjà celle demandée à cet
 * endroit. Rien n'est écrit pour tout l'appel.
 *
 * WHY refuser plutôt que sauter l'occurrence : l'éditeur refuse une étape sans
 * effet, et l'écarter en silence ferait rendre un reçu `ok` sur un compte
 * d'occurrences « écrites » que le document n'a pas reçues.
 */
function refusSansEffet(couples: readonly CoupleARemplacer[], releve: ReleveDuRemplacement, sansEffet: readonly OccurrenceARemplacer[]): RefusDuRemplacement {
  const rangs = [...new Set(sansEffet.map((occurrence) => occurrence.couple))];
  const duCouple = (rang: number): OccurrenceARemplacer[] => sansEffet.filter((occurrence) => occurrence.couple === rang);
  const descriptions = rangs.map((rang) => `${citer(couples[rang]!.find)} → ${citer(couples[rang]!.replace)}: blocks ${direLesBlocs(duCouple(rang))}.`);
  return {
    code: 'REPLACE_EVERYWHERE_NOTHING_TO_CHANGE',
    message: `replace_text everywhere wrote nothing: ${sansEffet.length} occurrence(s) already read exactly as their replacement `
      + '(find and replace differ only by the kind of a space or an apostrophe, and the document already has the one you ask for there). '
      + `${direLesCouples(descriptions)} `
      + 'Nothing was written and your scope is intact. Next step: remove these edits from the call if nothing else must change for them; '
      + `to change the other blocks that hold the same text, use apply_edit_plan (one text_rewrite step per block). ${PORTEE_DITE}`,
    pairs: rangs.map((rang) => {
      const emplacements = duCouple(rang);
      return coupleAuRecu(couples[rang]!, releve.parCouple[rang]!, emplacements, emplacements.filter((occurrence) => !estAEcrire(occurrence)).length);
    }),
  };
}

/**
 * Le refus d'un appel dont des occurrences ne peuvent pas être écrites : par
 * nature ({@link raisonHorsDePortee}), ou parce que l'aperçu de l'éditeur ne
 * les prouve pas (`cause` : ce qu'il en dit). Rien n'est écrit ; chaque bloc
 * est nommé, avec ce qu'il faut en faire.
 */
export function refusHorsDePortee(
  couples: readonly CoupleARemplacer[],
  releve: ReleveDuRemplacement,
  enCause: ReadonlyArray<{ occurrence: OccurrenceARemplacer; cause?: string }>,
): RefusDuRemplacement {
  const rangs = [...new Set(enCause.map(({ occurrence }) => occurrence.couple))];
  const descriptions = rangs.map((rang) => {
    const duCouple = enCause.filter(({ occurrence }) => occurrence.couple === rang);
    const blocs = duCouple.slice(0, CANVAS_REPLACE_LOCATIONS_MAX).map(({ occurrence, cause }) => (
      `block ${occurrence.nodeId ?? '(no identifier)'}: ${cause ?? (occurrence.horsDePortee === undefined ? 'the editor does not locate its text there' : SUITE_PAR_RAISON[occurrence.horsDePortee])}`
    ));
    return `${citer(couples[rang]!.find)} — ${blocs.join(' ; ')}${duCouple.length > blocs.length ? ` ; and ${duCouple.length - blocs.length} more` : ''}.`;
  });
  return {
    code: 'REPLACE_EVERYWHERE_OUT_OF_REACH',
    message: `replace_text everywhere wrote nothing: every count matches, but ${enCause.length} occurrence(s) cannot be written by this mode. `
      + `${direLesCouples(descriptions)} `
      + 'Nothing was written and your scope is intact. Next step: read each named block again (canvas_inspect), then replace the text there with apply_edit_plan '
      + '(one text_rewrite step per occurrence, find widened with its context until it occurs once in the block; for a table cell, target the table block that holds it), '
      + 'then resend this call with each expectedCount reduced by the occurrences already replaced. '
      + `If a block cannot be targeted, tell the lawyer which occurrence stays to change by hand. ${PORTEE_DITE}`,
    pairs: rangs.map((rang) => {
      const emplacements = enCause.filter(({ occurrence }) => occurrence.couple === rang).map(({ occurrence }) => occurrence);
      return coupleAuRecu(couples[rang]!, releve.parCouple[rang]!, emplacements, emplacements.length);
    }),
  };
}

/**
 * Les étapes du plan, une par occurrence : dans l'ordre du document par
 * paragraphe, et de la DERNIÈRE occurrence à la première dans chaque
 * paragraphe — une réécriture ne déplace alors aucune occurrence encore à
 * écrire, que l'éditeur résolve ses étapes d'avance ou l'une après l'autre.
 *
 * L'adresse est celle d'une étape `text_rewrite` d'`apply_edit_plan`
 * (`editPlanExecutor.ts`) — le texte, une fois exactement, dans son paragraphe —
 * avec le texte DU DOCUMENT pour motif. L'identifiant d'une étape est `r`, le
 * rang de son couple, puis le rang de l'occurrence parmi celles de ce couple.
 *
 * @param occurrences Les occurrences à écrire, dans l'ordre du document ({@link verdictDesComptes}).
 */
export function etapesDeRemplacement(occurrences: ReadonlyArray<OccurrenceARemplacer & { nodeId: string }>): EtapeEtSonOccurrence[] {
  const rangDansLeCouple = new Map<number, number>();
  const nommees = occurrences.map((occurrence) => {
    const rang = rangDansLeCouple.get(occurrence.couple) ?? 0;
    rangDansLeCouple.set(occurrence.couple, rang + 1);
    return { occurrence, id: `r${occurrence.couple}-${rang}` };
  });
  // Tri stable : l'ordre des paragraphes reste celui du document, seules les occurrences d'un même paragraphe s'inversent.
  return [...nommees]
    .sort((a, b) => a.occurrence.paragraphe - b.occurrence.paragraphe || b.occurrence.debut - a.occurrence.debut)
    .map(({ occurrence, id }): EtapeEtSonOccurrence => ({
      occurrence,
      etape: {
        id,
        op: 'text.rewrite',
        where: {
          by: 'select',
          select: { type: 'text', pattern: occurrence.texte, caseSensitive: true },
          within: { kind: 'block', nodeType: 'paragraph', nodeId: occurrence.nodeId },
          require: 'exactlyOne',
        },
        args: { replacement: { text: occurrence.remplacement } },
      },
    }));
}

/**
 * Le texte que chaque paragraphe réécrit doit porter après le plan, par
 * identifiant : ses occurrences remplacées, le reste intact. C'est à lui que
 * la relecture du fichier confronte chaque paragraphe.
 *
 * @param acte L'acte lu AVANT l'écriture, celui où les occurrences ont été trouvées.
 */
export function textesAttendus(acte: ActeARemplacer, occurrences: ReadonlyArray<OccurrenceARemplacer & { nodeId: string }>): Map<string, string> {
  const attendus = new Map<string, string>();
  for (const occurrence of [...occurrences].sort((a, b) => a.paragraphe - b.paragraphe || b.debut - a.debut)) {
    const courant = attendus.get(occurrence.nodeId) ?? texteDuParagraphe(acte.paragraphes[occurrence.paragraphe]!);
    attendus.set(occurrence.nodeId, `${courant.slice(0, occurrence.debut)}${occurrence.remplacement}${courant.slice(occurrence.fin)}`);
  }
  return attendus;
}
